
/**
 * Unit Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { UnitFormSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class UnitService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                focus: body.focus,
                message: body.message || 'Unit listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch unit list');
        }
    }

    /**
     * Resolve pagination page for an entity in the default list order (createdAt desc).
     * @param {{ focusEntityId: string, pageSize?: number, tenantId?: string }} params
     */
    async locateInList({ focusEntityId, pageSize = 10, tenantId }) {
        try {
            const response = await withRetry(
                () => this.api.getAll({ focusEntityId, pageSize, tenantId }),
                { maxRetries: 2 }
            );
            return {
                success: true,
                focus: response.focus ?? { found: false },
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to locate unit in list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch unit');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {

        // Rule: Validation at boundary
        const validation = UnitFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;


        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Unit oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create unit');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {

        // Validate before update
        const validation = UnitFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;


        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Unit güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update unit');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Unit silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete unit');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            // Use optimized batch delete endpoint
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            // Fallback to sequential delete if batch endpoint fails
            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} öğe silindi`
            };
        }
    }

    /**
     * Transfer units to a new parent
     * @param {Object} data - { unitIds: number[], newParentId: number|null }
     * @returns {Promise<Object>}
     */
    async transfer(data) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/transfer`, data);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Departmanlar başarıyla transfer edildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to transfer units');
        }
    }

    async assignUsers(unitId, userIds) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${unitId}/assign-users`, { userIds });
            return {
                success: true,
                data: response.data,
                message: response.data?.message || 'Kullanıcılar departmana atandı'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to assign users to unit');
        }
    }

    async unassignUser(unitId, userId) {
        try {
            const response = await this.api.client.post(`${this.api.basePath}/${unitId}/unassign-user/${userId}`);
            return {
                success: true,
                data: response.data,
                message: response.data?.message || 'Kullanıcı departmandan çıkarıldı'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to unassign user from unit');
        }
    }

    /**
     * Get units for dropdown selection
     * @returns {Promise<Object>}
     */
    async getUserCounts() {
        try {
            const response = await withRetry(
                () => this.api.client.get(`${this.api.basePath}/user-counts`),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data?.data || [],
                message: response.data?.message || 'Unit user counts fetched'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch unit user counts');
        }
    }

    async getDropdownParams() {
        logDropdownParamsInvocation('UnitService');
        try {
            const response = await withRetry(
                () => this.api.client.get(`${this.api.basePath}/dropdown/params`),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data?.data || [],
                message: 'Dropdown verisi getirildi'
            };
        } catch (error) {
            // Fallback: use getAll without pagination to populate dropdown
            try {
                const response = await this.getAll({ pageSize: 1000 });
                const dropdownData = (response.data || []).map(d => ({
                    value: d._id || d.id,
                    label: d.name || d.unitName
                }));
                return {
                    success: true,
                    data: dropdownData,
                    message: 'Dropdown verisi getirildi (fallback)'
                };
            } catch (fallbackError) {
                throw this._handleError(fallbackError, 'Failed to fetch dropdown data');
            }
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
